import type { DraftLine } from "@/features/lines/draft/useLineDraftState";
import type { ValidationIssue } from "@/types/line-editor.types";
import { formatIssue } from "./issue-messages";

const CODE_REGEX = /^[A-Za-z0-9_-]+$/;

export function computeCreationIssues(draft: DraftLine): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const machinesArr = Object.values(draft.machines);

  if (!draft.header.name.trim()) {
    const f = formatIssue("header:name-required");
    issues.push({
      id: "header:name-required",
      message: f.message,
      detail: f.detail,
      group: "header",
      severity: "danger",
      target: { kind: "header", field: "name" },
    });
  }

  if (machinesArr.length === 0) {
    const f = formatIssue("machines:empty");
    issues.push({
      id: "machines:empty",
      message: f.message,
      detail: f.detail,
      group: "machines",
      severity: "danger",
    });
  }

  for (const m of machinesArr) {
    const ctx = { machineName: m.basic.name, machineCode: m.basic.code };
    if (!m.basic.name.trim()) {
      const f = formatIssue("machine:name-empty", ctx);
      issues.push({
        id: `machine:${m.tmpId}:name-empty`,
        message: f.message,
        detail: f.detail,
        group: "machines",
        severity: "danger",
        target: { kind: "machine", id: m.tmpId, field: "name" },
      });
    }
    if (!m.basic.code.trim()) {
      const f = formatIssue("machine:code-empty", ctx);
      issues.push({
        id: `machine:${m.tmpId}:code-empty`,
        message: f.message,
        detail: f.detail,
        group: "machines",
        severity: "danger",
        target: { kind: "machine", id: m.tmpId, field: "code" },
      });
    } else if (!CODE_REGEX.test(m.basic.code)) {
      const f = formatIssue("machine:code-invalid", ctx);
      issues.push({
        id: `machine:${m.tmpId}:code-invalid`,
        message: f.message,
        detail: f.detail,
        group: "machines",
        severity: "danger",
        target: { kind: "machine", id: m.tmpId, field: "code" },
      });
    }
  }

  const usedMachineIds = new Set(
    draft.canvas.nodes
      .filter((n) => n.type === "MACHINE" && n.machineTmpId)
      .map((n) => n.machineTmpId as string)
  );
  for (const m of machinesArr) {
    if (!usedMachineIds.has(m.tmpId)) {
      const f = formatIssue("machine:orphan", { machineName: m.basic.name });
      issues.push({
        id: `machine:${m.tmpId}:orphan`,
        message: f.message,
        detail: f.detail,
        group: "canvas",
        severity: "warning",
        target: { kind: "machine", id: m.tmpId },
      });
    }
  }

  const edgesByNode = new Map<string, number>();
  for (const e of draft.canvas.edges) {
    edgesByNode.set(e.sourceNodeTmpId, (edgesByNode.get(e.sourceNodeTmpId) ?? 0) + 1);
    edgesByNode.set(e.targetNodeTmpId, (edgesByNode.get(e.targetNodeTmpId) ?? 0) + 1);
  }
  for (const n of draft.canvas.nodes) {
    if (n.type !== "MACHINE") continue;
    if ((edgesByNode.get(n.tmpId) ?? 0) === 0) {
      const f = formatIssue("canvas:node:no-link", { nodeLabel: n.label });
      issues.push({
        id: `canvas:node:${n.tmpId}:no-link`,
        message: f.message,
        detail: f.detail,
        group: "canvas",
        severity: "warning",
        target: { kind: "node", id: n.tmpId },
      });
    }
  }

  const startNode = draft.canvas.nodes.find((n) => n.type === "START");
  const endNode = draft.canvas.nodes.find((n) => n.type === "END");
  if (startNode && (edgesByNode.get(startNode.tmpId) ?? 0) === 0) {
    const f = formatIssue("canvas:start-disconnected");
    issues.push({
      id: "canvas:start-disconnected",
      message: f.message,
      detail: f.detail,
      group: "canvas",
      severity: "danger",
      target: { kind: "node", id: startNode.tmpId },
    });
  }
  if (endNode && (edgesByNode.get(endNode.tmpId) ?? 0) === 0) {
    const f = formatIssue("canvas:end-disconnected");
    issues.push({
      id: "canvas:end-disconnected",
      message: f.message,
      detail: f.detail,
      group: "canvas",
      severity: "danger",
      target: { kind: "node", id: endNode.tmpId },
    });
  }

  return issues;
}
